import React from "react";

import { ANIMATED_ICONS, type AnimatedIconName } from "@perceptron/ui/components/animations/animation-icons";
import { Icon, type IconName } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";

import { strings } from "@demo/utils/strings";

import { ShimmerText } from "./ShimmerText";

export interface ToolCallCardProps {
  label: string;
  icon: IconName;
  /** One line shown beside the pill while collapsed, e.g. the quoted query. */
  summary?: string;
  /** Shimmers the summary while the call is still running. */
  isStreaming: boolean;
  expanded: boolean;
  /** Keep the summary beside the chevron while expanded (the visit design does; search hides it). */
  keepSummaryWhenExpanded?: boolean;
  /** The summary's colour once the call has settled; while streaming it is always primary with the shimmer. */
  settledSummaryTone?: "primary" | "secondary";
  /** `error`: the failed design — red border and text, ghost pill, no chevron, not expandable. */
  tone?: "default" | "error";
  onToggle: () => void;
  children?: React.ReactNode;
  className?: string;
}

const isAnimatedIcon = (name: IconName): name is AnimatedIconName => name in ANIMATED_ICONS;

/**
 * The pill: icon and status label. It stays one element for the card's life, so settling changes it in
 * place: a looping icon finishes the cycle it is on and stops there (a card that mounts settled shows the
 * still icon), and the pill's width eases to the new label rather than jumping.
 */
function StatusPill({
  icon,
  label,
  isStreaming,
  isError,
}: {
  icon: IconName;
  label: string;
  isStreaming: boolean;
  isError: boolean;
}) {
  const [animatedIcon] = React.useState(() => (isStreaming && isAnimatedIcon(icon) ? icon : null));
  const iconRef = React.useRef<SVGSVGElement>(null);
  React.useEffect(() => {
    if (isStreaming) return;
    // End each endless loop after the iteration it is on; the icon then rests on that cycle's last frame.
    for (const animation of iconRef.current?.getAnimations?.({ subtree: true }) ?? []) {
      const timing = animation.effect?.getComputedTiming();
      if (timing?.iterations !== Infinity) continue;
      animation.effect?.updateTiming({ iterations: (timing.currentIteration ?? 0) + 1 });
    }
  }, [isStreaming]);

  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [labelWidth, setLabelWidth] = React.useState<number>();
  React.useLayoutEffect(() => {
    const node = labelRef.current;
    if (!node) return;
    const measure = () => setLabelWidth(node.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const tint = isError ? "text-error" : "text-foreground-primary";
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center gap-1 rounded-3xl py-1 pl-2 pr-3",
        isError ? "bg-error/30" : "bg-white/15",
      )}
    >
      {animatedIcon ? (
        <Icon animated aria-hidden className={cn("size-4", tint)} name={animatedIcon} ref={iconRef} />
      ) : (
        <Icon className={cn("size-4", tint)} name={icon} />
      )}
      {/* The outer span eases between label widths; the inner one is measured at its natural width. */}
      <span
        className="overflow-hidden transition-[width] duration-200 ease-out"
        style={labelWidth === undefined ? undefined : { width: labelWidth }}
      >
        <span className={cn("inline-block whitespace-nowrap label-2", tint)} ref={labelRef}>
          {label}
        </span>
      </span>
    </span>
  );
}

/** How long opening and closing take; the class and the unmount delay must agree. */
const DISCLOSURE_MS = 300;
// Transition duration only: `duration-300` also sets animation-duration (tailwindcss-animate), which sped the
// summary's shimmer up to a 0.3 s sweep.
const DISCLOSURE_DURATION_CLASS = "[transition-duration:300ms]";

/**
 * Open/closed state that can animate: the body mounts as soon as the card opens, starts closed for one
 * frame so the opening has somewhere to grow from, and stays mounted until the closing has finished.
 */
function useDisclosure(expanded: boolean) {
  const [open, setOpen] = React.useState(expanded);
  const [closing, setClosing] = React.useState(false);
  const wasExpanded = React.useRef(expanded);
  React.useEffect(() => {
    const opened = wasExpanded.current;
    wasExpanded.current = expanded;
    if (expanded) {
      setClosing(false);
      // Two frames: the first paints the body at 0fr, the second lets it transition to 1fr.
      let second = 0;
      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setOpen(true));
      });
      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }
    setOpen(false);
    // Only a card that was open has anything to close.
    if (!opened) return;
    setClosing(true);
    const timer = setTimeout(() => setClosing(false), DISCLOSURE_MS);
    return () => clearTimeout(timer);
  }, [expanded]);
  // `open` covers the render right after collapsing, before the effect has marked the card as closing: without
  // it the body would unmount for a frame and reappear already closed, and the close would not animate.
  return { bodyMounted: expanded || open || closing, open };
}

/**
 * The shell every tool call renders in: a bordered, blurred pill that opens into a card.
 * Collapsed it is one row (pill, summary, chevron); expanded the header keeps the pill and
 * chevron and the body renders below.
 */
function ToolCallCard({
  label,
  icon,
  summary,
  isStreaming,
  expanded,
  keepSummaryWhenExpanded = false,
  settledSummaryTone = "primary",
  tone = "default",
  onToggle,
  children,
  className,
}: ToolCallCardProps) {
  const isError = tone === "error";
  const { bodyMounted, open } = useDisclosure(expanded);
  // The collapsed card is a pill: its corners show at half its height whatever radius is asked for. Opening
  // morphs from that shown radius (measured) to the card's, instead of from a larger nominal one.
  const headerRef = React.useRef<HTMLButtonElement>(null);
  const [pillRadius, setPillRadius] = React.useState<number>();
  React.useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header || open) return;
    const measure = () => {
      // The collapsed card has no padding, so it is the header plus its 1px border on each side.
      if (header.offsetHeight > 0) setPillRadius((header.offsetHeight + 2) / 2);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [open]);
  const hideSummary = open && !keepSummaryWhenExpanded;
  const pill = <StatusPill icon={icon} isError={isError} isStreaming={isStreaming} label={label} />;
  const chevron = (
    <Icon
      className={cn(
        "size-4 shrink-0 text-foreground-primary transition-transform ease-out motion-reduce:transition-none",
        DISCLOSURE_DURATION_CLASS,
        { "rotate-90": open },
      )}
      name="chevron-right"
    />
  );
  const ariaLabel = formatString(expanded ? strings.toolCall.ariaLabels.collapse : strings.toolCall.ariaLabels.expand);

  if (isError) {
    // A failed call is one row that cannot be opened: the pill and the error line, no chevron.
    return (
      <div
        className={cn(
          "my-2 flex w-full transform-gpu items-center overflow-hidden rounded-[40px] border border-error bg-black/10 py-1 pl-1 pr-3 backdrop-blur-[4px]",
          className,
        )}
        data-testid="tool-call-card"
        data-tone="error"
      >
        {pill}
        <span className="flex min-w-px flex-1 items-center justify-end pl-8">
          <span className="min-w-px truncate text-error label-2" data-testid="tool-call-summary" title={summary}>
            {summary}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // Arbitrary radii need a compositing layer to clip on iOS Safari.
        "my-2 flex w-full transform-gpu flex-col overflow-hidden border border-foreground-primary bg-black/10 backdrop-blur-[4px]",
        "transition-[padding,border-radius,gap] ease-out motion-reduce:transition-none",
        DISCLOSURE_DURATION_CLASS,
        open ? "gap-1 rounded-[18px] p-2" : "gap-0 rounded-[40px] p-0",
        className,
      )}
      data-expanded={expanded}
      data-testid="tool-call-card"
      style={!open && pillRadius !== undefined ? { borderRadius: pillRadius } : undefined}
    >
      <button
        aria-expanded={expanded}
        aria-label={ariaLabel}
        className={cn(
          "flex w-full cursor-pointer items-center text-left transition-[padding] ease-out motion-reduce:transition-none",
          DISCLOSURE_DURATION_CLASS,
          open ? "justify-between py-0 pl-0 pr-2" : "py-1 pl-1 pr-3",
        )}
        ref={headerRef}
        type="button"
        onClick={onToggle}
      >
        {pill}
        {/* One header in both states, so the chevron rotates and the summary fades rather than being swapped.
            The summary sits against the chevron, right-justified, and truncates from its left edge when the
            row is too narrow; opened, it fades out as the body's copy of it fades in (unless it is kept). */}
        <span className="flex min-w-px flex-1 items-center justify-end gap-2 pl-8">
          <ShimmerText
            aria-hidden={hideSummary || undefined}
            className={cn(
              "min-w-px truncate transition-opacity ease-out label-2 motion-reduce:transition-none",
              DISCLOSURE_DURATION_CLASS,
              { "opacity-0": hideSummary },
            )}
            data-testid="tool-call-summary"
            isStreaming={isStreaming}
            settledTone={settledSummaryTone}
          >
            {summary}
          </ShimmerText>
          {chevron}
        </span>
      </button>
      {/* The body's row grows from 0fr to 1fr, so opening and closing animate its real height. */}
      {bodyMounted && (
        <div
          className={cn(
            "grid transition-[grid-template-rows] ease-out motion-reduce:transition-none",
            DISCLOSURE_DURATION_CLASS,
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div
            className={cn(
              "min-h-0 overflow-hidden transition-[opacity,transform] ease-out motion-reduce:transition-none",
              DISCLOSURE_DURATION_CLASS,
              open ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
            )}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

export default ToolCallCard;
